import { Color, Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon, ChevronUpIcon } from '@persona-id/design-tokens/icons';
import { noop } from 'lodash/fp';
import { KeyboardEventHandler } from 'react';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import SearchInput from 'dashboard/components-lib/SearchInput';
import { BASE } from 'design-system/variables/spacing';

const MultiSearchInputContainer = styled('div')`
  min-width: 400px;
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.color.background.primary};
  padding: ${Spacing[2]};
  margin-left: ${Spacing[1]};
  border-width: 1px;
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const StyledButton = styled('button')`
  background-color: ${Color.PrimaryWhite};
  width: ${Spacing[4]};
  height: ${Spacing[4]};
  color: ${Color.CoreSlate700};
`;

const StyledSearchInput = styled(SearchInput)`
  width: 100%;
`;

const SearchNavigation = styled('div')`
  display: flex;
  padding: ${SPACINGS.base}px;
  align-items: center;
  height: 100%;
`;

const IndexLabel = styled('p')`
  display: flex;
  justify-content: center;
  width: ${BASE * 10}px;
  color: ${Color.CoreSlate800};
  margin: 0px ${SPACINGS.base / 2}px;
  padding-top: ${SPACINGS.base / 2}px;
  white-space: nowrap;
`;

interface MultiSearchInputProps {
  className?: string;
  handleInput: (value: string) => void;
  handleOnNext: () => void;
  handleOnPrevious: () => void;
  handleOnEscape?: () => void;
  currentIndex?: number;
  length?: number;
  id?: string;
  value: string;
  placeholder: string;
  dataTest?: string;
}

const MultiSearchInput = ({
  className,
  handleInput,
  handleOnNext,
  handleOnPrevious,
  handleOnEscape = noop,
  currentIndex = 0,
  length = 0,
  id,
  value,
  placeholder,
  dataTest,
}: MultiSearchInputProps) => {
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.shiftKey && e.key === 'Enter') {
      handleOnPrevious();
    } else if (e.key === 'Enter') {
      handleOnNext();
    } else if (e.key === 'Escape') {
      handleOnEscape();
    }
  };

  return (
    <MultiSearchInputContainer tabIndex={0} onKeyDown={handleKeyDown}>
      <StyledSearchInput
        id={id}
        className={className}
        handleInput={handleInput}
        value={value}
        placeholder={placeholder}
        dataTest={dataTest}
        title={null}
        autoFocus
      />
      <SearchNavigation>
        <StyledButton
          data-test='document-viewer__multi-search-input--previous-button'
          id='document-viewer__multi-search-input--previous-button'
          onClick={() => handleOnPrevious()}
        >
          <ChevronUpIcon />
        </StyledButton>
        <IndexLabel>{`${currentIndex} of ${length}`}</IndexLabel>
        <StyledButton
          data-test='document-viewer__multi-search-input--next-button'
          id='document-viewer__multi-search-input--next-button'
          onClick={() => handleOnNext()}
        >
          <ChevronDownIcon />
        </StyledButton>
      </SearchNavigation>
    </MultiSearchInputContainer>
  );
};

export default MultiSearchInput;
